import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableRow, TableCell } from '@mui/material';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Panel from '../reusable/Panel';
import Loader from '../reusable/Loader';
import useUserDetails from '../hooks/useUserDetails';
import { getConfiguredSchools } from '../../services/eoaConfigServices';
import { getDataIngestionWorkflowStatus, executeWorkflowTransition } from '../../services/workflowService';
import {
  getFocusGroupWorkflowStatus,
  executeFocusGroupWorkflowTransition,
} from '../../services/focusGroupWorkflowService';
import {
  getSchoolReportWorkflowStatus,
  executeSchoolReportWorkflowTransition,
  generateSchoolReport,
} from '../../services/workflowService';
import {
  getSurveyReportWorkflowStatus,
  executeSurveyReportWorkflowTransition,
  generateSurveyReport,
} from '../../services/surveyReportWorkflowService';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import useErrorModal from '../hooks/useErrorModal';
import DistrictDataIngestionStatus from '../data qc/DistrictDataIngestionStatus';
import FocusGroupReportStatus from '../focus-groups/FocusGroupReportStatus';
import SchoolReportStatus from '../school-reports/SchoolReportStatus';
import SurveyReportStatus from '../survey-reports/SurveyReportStatus';
import ViewSchoolReportLink from '../school-reports/ViewSchoolReportLink';
import ViewSurveyReportLink from '../survey-reports/ViewSurveyReportLink';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './DeveloperDashboard.css';

interface SchoolWorkflowStatuses {
  schoolId: string;
  schoolName: string;
  focusGroupStatus: string | null;
  schoolReportStatus: string | null;
  surveyReportStatus: string | null;
}

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const [loading, setLoading] = useState(true);
  const [districtWorkflowStatus, setDistrictWorkflowStatus] = useState<string | null>(null);
  const [schoolWorkflows, setSchoolWorkflows] = useState<SchoolWorkflowStatuses[]>([]);
  const [processingTransitions, setProcessingTransitions] = useState<Record<string, boolean>>({});

  const fetchWorkflowStatuses = useCallback(async () => {
    if (!userDetails) return;

    try {
      setLoading(true);

      const districtWorkflowResponse = await getDataIngestionWorkflowStatus(
        userDetails.eoaId,
        userDetails.access_token
      );
      setDistrictWorkflowStatus(districtWorkflowResponse.current_state);

      const schoolsResponse = await getConfiguredSchools(userDetails.eoaId, userDetails.access_token);
      const configuredSchools = schoolsResponse.data;

      const schoolStatusesPromises = configuredSchools.map(async (school: ConfiguredEOASchool) => {
        let focusGroupStatus = null;
        let schoolReportStatus = null;
        let surveyReportStatus = null;

        try {
          const fgStatus = await getFocusGroupWorkflowStatus(userDetails.eoaId, school.id, userDetails.access_token);
          focusGroupStatus = fgStatus.current_state;
        } catch (error) {
          console.error('Error fetching focus group workflow status:', error);
        }

        try {
          const srStatus = await getSchoolReportWorkflowStatus(userDetails.eoaId, school.id, userDetails.access_token);
          schoolReportStatus = srStatus.current_state;
        } catch (error) {
          console.error('Error fetching school report workflow status:', error);
        }

        try {
          const surveyReportStatusResponse = await getSurveyReportWorkflowStatus(
            userDetails.eoaId,
            school.id,
            userDetails.access_token
          );
          surveyReportStatus = surveyReportStatusResponse.current_state;
        } catch (error) {
          console.error('Error fetching survey report workflow status:', error);
        }

        return {
          schoolId: school.id,
          schoolName: school.name,
          focusGroupStatus,
          schoolReportStatus,
          surveyReportStatus,
        };
      });

      const schoolStatuses = await Promise.all(schoolStatusesPromises);
      setSchoolWorkflows(schoolStatuses);
    } catch (error) {
      console.error('Error fetching workflow statuses:', error);
      handleOpenErrorModal('Error fetching workflow statuses', error as Error);
    } finally {
      setLoading(false);
    }
  }, [userDetails, handleOpenErrorModal]);

  useEffect(() => {
    fetchWorkflowStatuses();
  }, [fetchWorkflowStatuses]);

  const handleDistrictWorkflowTransition = async (transitionName: string) => {
    if (!userDetails) return;

    const key = `district-${transitionName}`;
    setProcessingTransitions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await executeWorkflowTransition(userDetails.eoaId, userDetails.access_token, transitionName);
      setDistrictWorkflowStatus(response.current_state);
    } catch (error) {
      console.error('Error executing district workflow transition:', error);
      handleOpenErrorModal('Error executing district workflow transition', error as Error);
    } finally {
      setProcessingTransitions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleFocusGroupTransition = async (schoolId: string, transitionName: string) => {
    if (!userDetails) return;

    const key = `fg-${schoolId}-${transitionName}`;
    setProcessingTransitions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await executeFocusGroupWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        transitionName
      );

      setSchoolWorkflows((prev) =>
        prev.map((school) =>
          school.schoolId === schoolId ? { ...school, focusGroupStatus: response.current_state } : school
        )
      );
    } catch (error) {
      console.error('Error executing focus group workflow transition:', error);
      handleOpenErrorModal('Error executing focus group workflow transition', error as Error);
    } finally {
      setProcessingTransitions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSchoolReportTransition = async (schoolId: string, transitionName: string) => {
    if (!userDetails) return;

    const key = `sr-${schoolId}-${transitionName}`;
    setProcessingTransitions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await executeSchoolReportWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        transitionName
      );

      setSchoolWorkflows((prev) =>
        prev.map((school) =>
          school.schoolId === schoolId ? { ...school, schoolReportStatus: response.current_state } : school
        )
      );
    } catch (error) {
      console.error('Error executing school report workflow transition:', error);
      handleOpenErrorModal('Error executing school report workflow transition', error as Error);
    } finally {
      setProcessingTransitions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleGenerateSchoolReport = async (schoolId: string) => {
    if (!userDetails) return;

    const key = `sr-generate-${schoolId}`;
    setProcessingTransitions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await generateSchoolReport(userDetails.eoaId, schoolId, userDetails.access_token);

      setSchoolWorkflows((prev) =>
        prev.map((school) =>
          school.schoolId === schoolId ? { ...school, schoolReportStatus: response.current_state } : school
        )
      );
    } catch (error) {
      console.error('Error generating school report:', error);
      handleOpenErrorModal('Error generating school report', error as Error);
    } finally {
      setProcessingTransitions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSurveyReportTransition = async (schoolId: string, transitionName: string) => {
    if (!userDetails) return;

    const key = `survey-${schoolId}-${transitionName}`;
    setProcessingTransitions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await executeSurveyReportWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        transitionName
      );

      setSchoolWorkflows((prev) =>
        prev.map((school) =>
          school.schoolId === schoolId ? { ...school, surveyReportStatus: response.current_state } : school
        )
      );
    } catch (error) {
      console.error('Error executing survey report workflow transition:', error);
      handleOpenErrorModal('Error executing survey report workflow transition', error as Error);
    } finally {
      setProcessingTransitions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleGenerateSurveyReport = async (schoolId: string) => {
    if (!userDetails) return;

    const key = `survey-generate-${schoolId}`;
    setProcessingTransitions((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await generateSurveyReport(userDetails.eoaId, schoolId, userDetails.access_token);

      setSchoolWorkflows((prev) =>
        prev.map((school) =>
          school.schoolId === schoolId ? { ...school, surveyReportStatus: response.current_state } : school
        )
      );
    } catch (error) {
      console.error('Error generating survey report:', error);
      handleOpenErrorModal('Error generating survey report', error as Error);
    } finally {
      setProcessingTransitions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const renderDistrictWorkflowSection = () => {
    const showInternalDataQcButton = districtWorkflowStatus === 'DISTRICT_DATA_UPLOAD_DONE';
    const key = 'district-internal_data_qc_manual_complete';

    const tableHeaderData: TableHeaderData<any>[] = [
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Actions' },
    ];

    return (
      <Panel className="developer-dashboard-panel">
        <h2 className="developer-dashboard-section-title">District Data Ingestion Workflow</h2>
        <CustomTable tableHeaderData={tableHeaderData}>
          <TableRow>
            <TableCell>
              <DistrictDataIngestionStatus status={districtWorkflowStatus} size="small" />
            </TableCell>
            <TableCell>
              {showInternalDataQcButton ? (
                <CustomButton
                  variant="primary"
                  buttonText="Mark Internal Data QC Complete"
                  buttonAction={() => handleDistrictWorkflowTransition('internal_data_qc_manual_complete')}
                  disabled={processingTransitions[key]}
                />
              ) : (
                <span className="developer-dashboard-no-action">No action available</span>
              )}
            </TableCell>
          </TableRow>
        </CustomTable>
      </Panel>
    );
  };

  const renderSchoolWorkflowsSection = () => {
    const focusGroupTableHeaders: TableHeaderData<any>[] = [
      { key: 'school', label: 'School' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Actions' },
    ];

    const schoolReportTableHeaders: TableHeaderData<any>[] = [
      { key: 'school', label: 'School' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Actions' },
    ];

    const surveyTableHeaders: TableHeaderData<any>[] = [
      { key: 'school', label: 'School' },
      { key: 'status', label: 'Status' },
      { key: 'actions', label: 'Actions' },
    ];

    return (
      <>
        <Panel className="developer-dashboard-panel">
          <h2 className="developer-dashboard-section-title">Focus Group Report Workflow</h2>
          {schoolWorkflows.length === 0 ? (
            <p>No schools found</p>
          ) : (
            <CustomTable tableHeaderData={focusGroupTableHeaders}>
              {schoolWorkflows.map((school) => {
                const showAction = school.focusGroupStatus === 'AI_FAILED';
                const key = `fg-${school.schoolId}-transcript_corrupted_developer_manual_intervention`;

                return (
                  <TableRow key={school.schoolId}>
                    <TableCell>{school.schoolName}</TableCell>
                    <TableCell>
                      <div className="developer-dashboard-status-container">
                        <FocusGroupReportStatus status={school.focusGroupStatus} size="small" />
                        {school.focusGroupStatus === 'AI_FAILED' && (
                          <span className="developer-dashboard-status-note">(AI Failed)</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {showAction ? (
                        <CustomButton
                          variant="primary"
                          buttonText="Mark Report Generated"
                          buttonAction={() =>
                            handleFocusGroupTransition(
                              school.schoolId,
                              'transcript_corrupted_developer_manual_intervention'
                            )
                          }
                          disabled={processingTransitions[key]}
                        />
                      ) : (
                        <span className="developer-dashboard-no-action">No action available</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </CustomTable>
          )}
        </Panel>

        <Panel className="developer-dashboard-panel">
          <h2 className="developer-dashboard-section-title">School Report Workflow</h2>
          {schoolWorkflows.length === 0 ? (
            <p>No schools found</p>
          ) : (
            <CustomTable tableHeaderData={schoolReportTableHeaders}>
              {schoolWorkflows.map((school) => {
                const isNotGenerated = school.schoolReportStatus === 'SCHOOL_REPORT_NOT_GENERATED';
                const isRejected = school.schoolReportStatus === 'SCHOOL_REPORT_REJECTED';
                const isGenerated = school.schoolReportStatus === 'SCHOOL_REPORT_GENERATED';
                const isFailed = school.schoolReportStatus === 'SCHOOL_REPORT_GENERATION_FAILED';
                const showTransitionAction = isRejected || isGenerated;
                const showGenerateAction = isNotGenerated || isFailed;
                const showViewReport = isGenerated;

                const transitionName = 'school_report_ready_for_review_manual_intervention';
                const transitionKey = `sr-${school.schoolId}-${transitionName}`;
                const generateKey = `sr-generate-${school.schoolId}`;

                return (
                  <TableRow key={school.schoolId}>
                    <TableCell>{school.schoolName}</TableCell>
                    <TableCell>
                      <div className="developer-dashboard-status-container">
                        <SchoolReportStatus status={school.schoolReportStatus} size="small" />
                        {isFailed && <span className="developer-dashboard-status-note">(failed)</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {showViewReport ? (
                        <ViewSchoolReportLink schoolId={school.schoolId} />
                      ) : showGenerateAction ? (
                        <CustomButton
                          variant="primary"
                          buttonText="Initiate Generation"
                          buttonAction={() => handleGenerateSchoolReport(school.schoolId)}
                          disabled={processingTransitions[generateKey]}
                        />
                      ) : showTransitionAction ? (
                        <CustomButton
                          variant="primary"
                          buttonText="Mark Ready for Review"
                          buttonAction={() => handleSchoolReportTransition(school.schoolId, transitionName)}
                          disabled={processingTransitions[transitionKey]}
                        />
                      ) : (
                        <span className="developer-dashboard-no-action">No action available</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </CustomTable>
          )}
        </Panel>

        <Panel className="developer-dashboard-panel">
          <h2 className="developer-dashboard-section-title">Survey Report Workflow</h2>
          {schoolWorkflows.length === 0 ? (
            <p>No schools found</p>
          ) : (
            <CustomTable tableHeaderData={surveyTableHeaders}>
              {schoolWorkflows.map((school) => {
                const status = school.surveyReportStatus;

                const isNotStarted = !status || status === 'SURVEYS_NOT_STARTED';
                const isOpen = status === 'SURVEYS_OPEN';
                const isClosed = status === 'SURVEYS_CLOSED';
                const isGenerated = status === 'SURVEY_REPORT_GENERATED';
                const isFinalized = status === 'SURVEY_REPORT_FINALIZED';
                const isRejected = status === 'SURVEY_REPORT_REJECTED';
                const isFailed = status === 'SURVEY_REPORT_GENERATION_FAILED';

                const distributeKey = `survey-${school.schoolId}-mark_surveys_as_distributed`;
                const closeKey = `survey-${school.schoolId}-close_surveys`;
                const approveKey = `survey-${school.schoolId}-approve_survey_report`;
                const rejectKey = `survey-${school.schoolId}-reject_survey_report`;
                const resolveKey = `survey-${school.schoolId}-resolve_survey_generation_issues`;
                const reopenKey = `survey-${school.schoolId}-reopen_surveys_for_data_issues`;
                const generateKey = `survey-generate-${school.schoolId}`;

                const canGenerate = isClosed;
                const canRegenerate = isGenerated || isFinalized || isRejected || isFailed;
                const canViewReport = isGenerated || isFinalized || isRejected;
                const hasAction =
                  isNotStarted || isOpen || isClosed || isGenerated || isFinalized || isRejected || isFailed;

                return (
                  <TableRow key={school.schoolId}>
                    <TableCell>{school.schoolName}</TableCell>
                    <TableCell>
                      <SurveyReportStatus status={school.surveyReportStatus} size="small" />
                    </TableCell>
                    <TableCell>
                      <div className="developer-dashboard-survey-report-actions">
                        {isNotStarted && (
                          <CustomButton
                            variant="primary"
                            buttonText="Mark Surveys as Distributed"
                            buttonAction={() =>
                              handleSurveyReportTransition(school.schoolId, 'mark_surveys_as_distributed')
                            }
                            disabled={processingTransitions[distributeKey]}
                          />
                        )}
                        {isOpen && (
                          <CustomButton
                            variant="primary"
                            buttonText="Close Surveys"
                            buttonAction={() => handleSurveyReportTransition(school.schoolId, 'close_surveys')}
                            disabled={processingTransitions[closeKey]}
                          />
                        )}
                        {canGenerate && (
                          <CustomButton
                            variant="primary"
                            buttonText="Generate Report"
                            buttonAction={() => handleGenerateSurveyReport(school.schoolId)}
                            disabled={processingTransitions[generateKey]}
                          />
                        )}
                        {canRegenerate && (
                          <CustomButton
                            variant="primary"
                            buttonText="Regenerate Report"
                            buttonAction={() => handleGenerateSurveyReport(school.schoolId)}
                            disabled={processingTransitions[generateKey]}
                          />
                        )}
                        {canViewReport && <ViewSurveyReportLink schoolId={school.schoolId} />}
                        {isGenerated && (
                          <>
                            <CustomButton
                              variant="primary"
                              buttonText="Approve"
                              buttonAction={() =>
                                handleSurveyReportTransition(school.schoolId, 'approve_survey_report')
                              }
                              disabled={processingTransitions[approveKey]}
                            />
                            <CustomButton
                              variant="text"
                              buttonText="Reject"
                              buttonAction={() => handleSurveyReportTransition(school.schoolId, 'reject_survey_report')}
                              disabled={processingTransitions[rejectKey]}
                            />
                          </>
                        )}
                        {isRejected && (
                          <>
                            <CustomButton
                              variant="primary"
                              buttonText="Resolve Issues"
                              buttonAction={() =>
                                handleSurveyReportTransition(school.schoolId, 'resolve_survey_generation_issues')
                              }
                              disabled={processingTransitions[resolveKey]}
                            />
                            <CustomButton
                              variant="text"
                              buttonText="Reopen Surveys"
                              buttonAction={() =>
                                handleSurveyReportTransition(school.schoolId, 'reopen_surveys_for_data_issues')
                              }
                              disabled={processingTransitions[reopenKey]}
                            />
                          </>
                        )}
                        {!hasAction && <span className="developer-dashboard-no-action">No action available</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </CustomTable>
          )}
        </Panel>
      </>
    );
  };

  if (!userDetails) return <Loader />;

  if (loading) return <Loader />;

  return (
    <div className="main-content">
      <CustomButton
        variant="text"
        buttonText="Back"
        icon={<img src={GoBackIcon} alt="go back" />}
        iconPosition="start"
        buttonAction={() => navigate('/overview')}
      />
      <div className="developer-dashboard-header">
        <h1 className="developer-dashboard-page-title">Developer Dashboard</h1>
        <p className="developer-dashboard-description">
          Manage workflow transitions for development and testing purposes
        </p>
      </div>
      {renderDistrictWorkflowSection()}
      {renderSchoolWorkflowsSection()}
    </div>
  );
};

export default DeveloperDashboard;
